import React from "react";
import { Box } from "@mui/material";

export interface ChipTheme {
  label: string;
  color: string;       // main solid color (border/dot)
  bgColor: string;     // soft background
  textColor: string;   // high contrast text
}

export const CONTENT_TYPE_MAP: Record<string, ChipTheme> = {
  // Combined Operation + Content Types
  "read-static": {
    label: "Static Reads",
    color: "#1976d2",
    bgColor: "#e3f2fd",
    textColor: "#0d47a1",
  },
  "read-dynamic": {
    label: "Dynamic Reads",
    color: "#43a047",
    bgColor: "#e8f5e9",
    textColor: "#1b5e20",
  },
  "read-static_media": {
    label: "Media Reads",
    color: "#81c784",
    bgColor: "#f1f8e9",
    textColor: "#33691e",
  },
  "write-dynamic": {
    label: "Dynamic Writes",
    color: "#ffa000",
    bgColor: "#fff8e1",
    textColor: "#b45309",
  },
  "write-static": {
    label: "Static Writes",
    color: "#e53935",
    bgColor: "#ffebee",
    textColor: "#b71c1c",
  },
  "write-static_media": {
    label: "Media Writes",
    color: "#ffcc80",
    bgColor: "#fff3e0",
    textColor: "#78350f",
  },

  // Base Content Types
  "static": {
    label: "Static Content",
    color: "#1976d2",
    bgColor: "#e3f2fd",
    textColor: "#0d47a1",
  },
  "dynamic": {
    label: "Dynamic Content",
    color: "#43a047",
    bgColor: "#e8f5e9",
    textColor: "#1b5e20",
  },
  "static_media": {
    label: "Media Content",
    color: "#81c784",
    bgColor: "#f1f8e9",
    textColor: "#33691e",
  },
};

interface ContentTypeChipProps {
  typeKey: string;
  size?: "small" | "medium";
  style?: React.CSSProperties;
  onClick?: () => void;
  icon?: React.ReactNode;
  "data-testid"?: string;
}

export const ContentTypeChip: React.FC<ContentTypeChipProps> = ({
  typeKey,
  size = "small",
  style,
  onClick,
  icon,
  "data-testid": dataTestId,
}) => {
  const theme = CONTENT_TYPE_MAP[typeKey] || {
    label: typeKey,
    color: "#9e9e9e",
    bgColor: "#f5f5f5",
    textColor: "#616161",
  };

  const isSmall = size === "small";

  return (
    <Box
      onClick={onClick}
      data-testid={dataTestId}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: isSmall ? 0.75 : 1,
        px: isSmall ? 1 : 1.5,
        py: isSmall ? 0.25 : 0.5,
        borderRadius: "4px", // Square chip style with slight roundness
        border: `1px solid ${theme.color}`,
        backgroundColor: theme.bgColor,
        color: theme.textColor,
        fontSize: isSmall ? "0.75rem" : "0.875rem",
        fontWeight: 600,
        lineHeight: 1.2,
        userSelect: "none",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s ease-in-out",
        marginRight: "5px",
        "&:hover": onClick
          ? {
            transform: "translateY(-1px)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
            filter: "brightness(0.95)",
          }
          : undefined,
        "&:active": onClick
          ? {
            transform: "translateY(0px)",
            filter: "brightness(0.9)",
          }
          : undefined,
        ...style,
      }}
    >
      {icon ? (
        icon
      ) : (
        <Box
          sx={{
            width: isSmall ? 6 : 8,
            height: isSmall ? 6 : 8,
            borderRadius: "50%",
            backgroundColor: theme.color,
            flexShrink: 0,
          }}
        />
      )}
      {theme.label}
    </Box>
  );
};
